/*!
 * cart-upsells.js — Régua em camadas do "Complete sua sacola".
 *
 * Fluxo:
 *   1. Clique no "+" é tratado por DELEGAÇÃO GLOBAL no `document` — funciona
 *      tanto no HTML renderizado pelo servidor quanto em qualquer re-render
 *      posterior. O bug histórico (listener só existia no nó recém-inserido
 *      pelo `refresh()`) fica impossível.
 *   2. `refresh()` refaz a régua via Section Rendering API só quando o drawer
 *      está aberto (lazy).
 *   3. Após adicionar, chama `window.SC.refreshCart` para repintar a sacola.
 */
(function () {
  "use strict";
  var inFlight = null;
  // null = ainda não rodou; garante que a 1ª execução NUNCA sai pela guarda
  // — mesmo que `currentSig()` devolva string vazia porque a lista ainda
  // não montou as linhas.
  var lastSig = null;
  var pendingAdds = new WeakSet();

  function drawer() { return document.querySelector("[data-cart-drawer]"); }
  function isDrawerOpen() { var d = drawer(); return !!(d && d.classList.contains("is-open")); }

  function currentSig() {
    var body = document.querySelector("[data-cart-body]");
    if (!body) return "";
    var keys = [];
    body.querySelectorAll("[data-line-key]").forEach(function (el) {
      var q = el.querySelector(".qty span");
      keys.push(el.getAttribute("data-line-key") + ":" + (q ? q.textContent.trim() : ""));
    });
    return keys.join("|");
  }

  async function refresh(opts) {
    opts = opts || {};
    var root = document.querySelector("[data-cart-upsells-root]");
    if (!root) return;
    var body = document.querySelector("[data-cart-body]");
    var hasItems = body && body.children.length > 0;
    if (!hasItems) {
      root.hidden = true;
      root.innerHTML = "";
      lastSig = null;
      return;
    }
    // LAZY: só faz o refetch pesado quando o drawer está aberto (ou quando
    // o caller pediu force). Isso evita rodar Liquid caro em toda mudança
    // do carrinho — economiza servidor e deixa a sacola responsiva.
    if (!opts.force && !isDrawerOpen()) return;
    var sig = currentSig();
    if (!opts.force && lastSig !== null && sig === lastSig && root.innerHTML.trim() !== "") return;
    if (inFlight) inFlight.abort();
    inFlight = new AbortController();
    var t0 = performance.now();
    try {
      // product_id + intent populam `recommendations` na section (CAMADA 3).
      // Sem eles o objeto vem vazio e a camada não contribui — por isso o
      // Liquid a guarda com `recommendations.performed`. Usamos o ÚLTIMO item
      // da sacola, mesma âncora que o React usa em fetchProductRecommendations.
      var ancora = "";
      try {
        var linhas = document.querySelectorAll("[data-cart-body] [data-product-id]");
        if (linhas.length) ancora = linhas[linhas.length - 1].getAttribute("data-product-id") || "";
      } catch (_) {}
      var url = "/?sections=cart-upsells" + (ancora ? "&product_id=" + encodeURIComponent(ancora) + "&intent=complementary" : "");
      var r = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: inFlight.signal,
        credentials: "same-origin",
      });
      if (!r.ok) return;
      var data = await r.json();
      var html = data && data["cart-upsells"];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, "text/html");
      var next = doc.querySelector("[data-cart-upsells-root]");
      if (!next) return;
      root.replaceWith(next);
      lastSig = sig;
      try {
        var ms = Math.round(performance.now() - t0);
        // Marcador leve para o QA runner cronometrar sem depender de network.
        console.debug("[cart-upsells] section render", ms + "ms");
      } catch (_) {}
    } catch (e) {
      if (e && e.name !== "AbortError") console.warn("[cart-upsells] refresh falhou", e);
    }
  }

  /* ── Mais vendidos do estado VAZIO ──────────────────────────────────────
     Espelha o que o CartDrawer do React faz (busca na Storefront API a cada
     abertura) e o que esta régua já fazia para a sacola cheia: recalcula no
     momento em que a cliente olha, em vez de servir o HTML congelado no
     carregamento da página.

     O bug que isto corrige: a grade era renderizada uma única vez, junto com a
     página, e filtrava por `p.available`. Se naquele instante o topo de
     'mais-vendidos' estava sem estoque, a cliente via a grade incompleta — ou
     nenhuma — e abrir/fechar a sacola não recuperava, só um reload. */
  var bestInFlight = null;
  var bestLastAt = 0;

  async function refreshBestSellers(opts) {
    opts = opts || {};
    var root = document.querySelector("[data-cart-best-root]");
    if (!root) return;
    // Só faz sentido com a sacola vazia: com itens, o bloco está oculto e quem
    // manda na tela é a régua de upsell.
    var body = document.querySelector("[data-cart-body]");
    if (body && body.children.length > 0) return;
    if (!opts.force && !isDrawerOpen()) return;
    // Coalesce: o MutationObserver dispara muito durante a animação de abrir.
    var agora = Date.now();
    if (!opts.force && agora - bestLastAt < 3000) return;
    if (bestInFlight) bestInFlight.abort();
    bestInFlight = new AbortController();
    try {
      var r = await fetch("/?sections=cart-bestsellers", {
        headers: { Accept: "application/json" },
        signal: bestInFlight.signal,
        credentials: "same-origin",
      });
      if (!r.ok) return;
      var data = await r.json();
      var html = data && data["cart-bestsellers"];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, "text/html");
      var next = doc.querySelector("[data-cart-best-root]");
      if (!next) return;
      // Não substituir por uma grade PIOR que a atual: se a section vier sem
      // cards (estoque zerou entre um render e outro), manter o que já está na
      // tela é melhor do que apagar produtos que a cliente estava vendo.
      var atual = Number(root.getAttribute("data-count") || 0);
      var novo = Number(next.getAttribute("data-count") || 0);
      if (novo === 0 && atual > 0) return;
      root.replaceWith(next);
      bestLastAt = agora;
      try {
        console.debug("[cart-best] section render", novo + " cards", next.getAttribute("data-source"));
      } catch (_) {}
    } catch (e) {
      if (e && e.name !== "AbortError") console.warn("[cart-best] refresh falhou", e);
    }
  }

  // ── DELEGAÇÃO GLOBAL ──
  // O container do drawer (e a régua de upsells) é reconstruído pelo Section
  // Rendering API a cada mudança do carrinho — listeners presos ao BOTÃO
  // morrem no primeiro re-render. Delegar no `document` sobrevive a qualquer
  // troca de árvore.
  document.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest && e.target.closest("[data-quick-add]");
    if (!btn) return;
    e.preventDefault();
    if (pendingAdds.has(btn) || btn.disabled) return;
    var vid = btn.dataset.vid;
    if (!vid) {
      btn.setAttribute("data-error", "1");
      btn.setAttribute("aria-label", "Indisponível");
      return;
    }
    pendingAdds.add(btn);
    btn.disabled = true;
    var prevLabel = btn.textContent;
    btn.textContent = "…";
    (async function () {
      try {
        // PRÉ-VENDA (#016): o Liquid marca data-prevenda no botão quando a
        // peça do card tem data de pré-venda. Mandamos a MESMA propriedade de
        // linha que a PDP manda, senão a peça chega ao checkout sem o aviso —
        // e a nota do drawer, que lê properties, também não apareceria.
        // Sem a marca, o corpo do POST fica idêntico ao de antes.
        // O Liquid já entrega o TEXTO pronto da propriedade, com a data
        // resolvida da variante. O JS não recalcula nada — recalcular aqui
        // seria uma terceira conta para divergir das outras.
        var item = { id: Number(vid), quantity: 1 };
        if (btn.dataset.prevenda) {
          item.properties = { "Pré-venda": btn.dataset.prevenda };
        }
        var r = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [item] }),
        });
        if (!r.ok) {
          var msg = "";
          try { var j = await r.clone().json(); msg = j && (j.description || j.message) || ""; } catch (_) {}
          console.warn("[cart-upsells] add.js falhou", r.status, msg);
          btn.setAttribute("data-error", "1");
          btn.textContent = "!";
          setTimeout(function () { btn.textContent = prevLabel; btn.removeAttribute("data-error"); }, 1600);
          return;
        }
        // Repinta a sacola (contador, itens, subtotal, barra de frete).
        if (window.SC && typeof window.SC.refreshCart === "function") {
          await window.SC.refreshCart();
        }
        // Só refetch pesado da régua se o drawer estiver aberto — senão,
        // será feito no próximo openDrawer.
        if (isDrawerOpen()) setTimeout(function () { refresh({ force: true }); }, 200);
        btn.textContent = "✓";
        setTimeout(function () { btn.textContent = prevLabel; }, 900);
      } catch (err) {
        console.error("[cart-upsells] erro no add", err);
        btn.textContent = "!";
        setTimeout(function () { btn.textContent = prevLabel; }, 1600);
      } finally {
        btn.disabled = false;
        pendingAdds.delete(btn);
      }
    })();
  });

  // Inert templates prevent native lazy-loading heuristics from fetching
  // recommendations while the transformed/offscreen drawer is still closed.
  function revealRecommendationImages() {
    var d = drawer();
    if (!isDrawerOpen()) return;
    d.querySelectorAll('template[data-cart-recommendation-image]').forEach(function (template) {
      var root = template.closest('[data-cart-best-root], [data-cart-upsells-root]');
      var body = d.querySelector('[data-cart-body]');
      var hasItems = !!(body && body.children.length);
      if (!root || root.hidden || (root.hasAttribute('data-cart-best-root') && hasItems) ||
          (root.hasAttribute('data-cart-upsells-root') && !hasItems)) return;
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  function boot() {
    var watched = null, openObserver = null, wasOpen = false;
    function refreshVisible() {
      clearTimeout(boot._t);
      boot._t = setTimeout(function () {
        if (!isDrawerOpen()) return;
        revealRecommendationImages();
        refresh();
        refreshBestSellers();
      }, 200);
    }
    function bindDrawer() {
      var d = drawer();
      if (d === watched) return;
      if (openObserver) openObserver.disconnect();
      watched = d; wasOpen = false;
      if (!d) return;
      function onOpen() {
        var open = isDrawerOpen();
        if (open && !wasOpen) {
          revealRecommendationImages();
          refreshVisible();
        }
        wasOpen = open;
      }
      openObserver = new MutationObserver(onOpen);
      openObserver.observe(d, {attributes:true, attributeFilter:['class']});
      onOpen();
    }
    // Observe replacements, but ignore price rendering and mutations elsewhere
    // on the storefront. Cart network/checkout/add handlers are unchanged.
    var mo = new MutationObserver(function (records) {
      bindDrawer();
      if (!isDrawerOpen()) return;
      var d = drawer(), cartChanged = false, imagesChanged = false;
      records.forEach(function (record) {
        var target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (!target || !d.contains(target)) return;
        imagesChanged = true;
        if (target.matches('[data-cart-body]') || target.closest('[data-cart-body]')) cartChanged = true;
        record.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && (node.matches('[data-cart-body]') || node.querySelector('[data-cart-body]'))) cartChanged = true;
        });
      });
      if (imagesChanged) revealRecommendationImages();
      if (cartChanged) refreshVisible();
    });
    mo.observe(document.body, {childList:true, subtree:true});
    bindDrawer();
    document.addEventListener('sc:cart-updated', refreshVisible);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
